import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import H3 from '@base/H3/H3';
import P1 from '@base/P1/P1';
import InfiniteList from '@comp/InfiniteList/InfiniteList';
import TestSeriesCard from '@comp/TestSeriesCard/TestSeriesCard';
import Dropdown from '@comp/Dropdown/Dropdown';
import { fetchTestSeries } from '@actions/plusCourses';
import { getTestSeries } from '@selectors/quizzes';
import { useSelectorToStore } from '@hooks/reduxHooks';
import useWindowSize from '@hooks/useWindowSize';
import useRefreshDataOnGloUpdate from '@hooks/useRefreshDataOnGloUpdate';
import { getImage } from '@utils/imageHelper';
import { getTestSeriesFeedKey } from '@utils/feedTypeHelper';
import pushRoute from '@utils/pushRoute';
import { Spacings, breakpoints } from '@styles/index';
import { getValidTestType } from '../../utils';
import { OPTIONS } from '../../constants';
import Header from '../Common/Header';
import Loader from '../Common/Loader';

const NoCourses = styled(H3)`
  margin-top: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_5B};
  color: var(--color-text-primary);
`;

const linkStyle = `margin-bottom: ${Spacings.SPACING_6B};`;

const DropdownWrapper = styled.div`
  grid-column: span 18;
  display: grid;
  grid-template-columns: repeat(18, 1fr);
  grid-column-gap: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_6B};

  @media only screen and (max-width: ${breakpoints.mobile}) {
    grid-column: span 11;
    grid-column-gap: ${Spacings.SPACING_2B};
    grid-template-columns: repeat(10, 1fr);
  }
`;

const StyledDropdown = styled(Dropdown)`
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin-bottom: ${Spacings.SPACING_6B};
    grid-column: span 11;
    height: ${Spacings.SPACING_12B};
  }
`;

const Empty = styled.div`
  grid-column: span 18;
  text-align: center;
  margin-top: ${Spacings.SPACING_24B};
`;

const EmptyImage = styled.img`
  user-drag: none;
  user-select: none;
`;

const TestListingLanding = ({ dataAnalytics, showAll, maxLimit }) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const {
    query: { goalUID, goalSlug, 'test-type': testType },
    pathname
  } = router;

  const testSeriesData = useSelectorToStore(getTestSeries);

  const { isMobile } = useWindowSize();

  const initalType = getValidTestType(testType);
  const [testSeriesType, setTestSeriesType] = useState(initalType);

  const optionSelect = (option) => {
    dispatch(fetchTestSeries(goalUID, option.value));
    setTestSeriesType(option.value);

    pushRoute(
      {
        pathname,
        query: {
          goalUID,
          goalSlug,
          'test-type': option.value
        }
      },
      undefined,
      { shallow: true }
    );
  };

  const getSelectedOption = () =>
    OPTIONS.find((option) => option.value === testSeriesType);

  const data = testSeriesData[getTestSeriesFeedKey(goalUID, testSeriesType)];
  const testSeriesList = showAll
    ? data.results
    : data.results.slice(0, maxLimit);

  const isFetching = data?.isFetching;
  const isEmpty = !isFetching && !testSeriesList.length;
  const isLessThenLimit = isEmpty ? true : data.results.length <= maxLimit;

  const loadMore = (resetWithoutEmpty, forceReset) => {
    return dispatch(
      fetchTestSeries(
        goalUID,
        testSeriesType,
        true,
        resetWithoutEmpty,
        forceReset
      )
    );
  };

  useRefreshDataOnGloUpdate(() => loadMore(true, true));

  const renderTestSeries = (test, index) => (
    <TestSeriesCard
      test={test}
      key={test.uid}
      linkStyle={linkStyle}
      index={index}
      dataAnalytics={dataAnalytics}
    />
  );

  const renderContent = () => {
    if (isEmpty) {
      return (
        <Empty>
          <EmptyImage src={getImage('empty.svg')} alt="empty-content" />
          <NoCourses>No test series found</NoCourses>
          <P1 color="var(--color-text-secondary)">
            The educator hasn’t added any courses yet. Try changing the filters
          </P1>
        </Empty>
      );
    }

    if (showAll) {
      return (
        <InfiniteList
          data={data}
          loadMore={loadMore}
          _renderItem={renderTestSeries}
          gridColumn="5/15"
        />
      );
    }

    if (isFetching) {
      return <Loader />;
    }

    return testSeriesList.map((test, index) => (
      <TestSeriesCard
        test={test}
        key={test.uid}
        linkStyle={linkStyle}
        index={index}
        dataAnalytics={dataAnalytics}
      />
    ));
  };

  const seeAllHref =
    isLessThenLimit || showAll
      ? null
      : `/goal/${goalSlug}/${goalUID}/test-series/all?test-type=${testSeriesType}`;

  return (
    <>
      <Header label="Test Series" seeAllHref={seeAllHref} />
      <DropdownWrapper>
        <StyledDropdown
          options={OPTIONS}
          onOptionClick={optionSelect}
          selectedOption={getSelectedOption()}
          menuWidth={isMobile ? '100%' : '176px'}
        />
      </DropdownWrapper>

      {renderContent()}
    </>
  );
};

export default TestListingLanding;

TestListingLanding.propTypes = {
  maxLimit: PropTypes.number,
  showAll: PropTypes.bool,
  dataAnalytics: PropTypes.string
};

TestListingLanding.defaultProps = {
  maxLimit: 6,
  showAll: true,
  dataAnalytics: ''
};
